import {useMemo, useEffect, useState, useRef, useCallback} from 'react'

import codeBlocksExtension from './extensions/code-blocks'
import imagesExtension from './extensions/images'
import mathExtension from './extensions/math'
import linksExtension from './extensions/links'
import {clsx} from 'clsx'
import styles from './MarkdownRenderer.module.css'
import {ExtensionContext} from './extensions/ExtensionContext'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkCjkFriendly from 'remark-cjk-friendly'
import remarkCjkFriendlyGfmStrikethrough from 'remark-cjk-friendly-gfm-strikethrough'
import type {PluggableList, Pluggable} from 'unified'

import {useCombinedComponents} from './combine-components'
import type {CopilotMarkdownExtension} from './extension'

import streamingExtension from './extensions/streaming'
import type {CopilotAnnotations} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'

// Lazy-load rehype-highlight + lowlight (~1 MB) to keep them out of the
// initial synchronous bundle. Returns the configured plugin once loaded.
let cachedHighlightPlugin: Pluggable | null = null
const highlightPluginPromise: Promise<Pluggable | null> = (async () => {
  if (IS_SERVER) return Promise.resolve(null)
  if (cachedHighlightPlugin !== null) return Promise.resolve(cachedHighlightPlugin)
  const [rehypeHighlightModule, lowlightModule] = await Promise.all([import('rehype-highlight'), import('lowlight')])
  const plugin: Pluggable = [rehypeHighlightModule.default, {languages: lowlightModule.all}]
  cachedHighlightPlugin = plugin
  return plugin
})()

function useRehypeHighlight(): Pluggable | null {
  const [plugin, setPlugin] = useState<Pluggable | null>(cachedHighlightPlugin)

  useEffect(() => {
    if (!cachedHighlightPlugin) {
      void (async () => {
        const p = await highlightPluginPromise
        setPlugin(p)
      })()
    }
  }, [])

  return plugin
}

/**
 * Apply this class to elements to suppress the fade-in animation. This should only be done as a workaround if there
 * is a conflicting animation already applied (ie, to `Spinner` components).
 */
export const disableStreamingFadeIn = styles.noFade

export interface MarkdownRendererProps {
  /** Markdown to render. */
  markdown: string
  /** Callback when a link is clicked. `preventDefault` to stop navigation. */
  onLinkClick?: (event: MouseEvent) => void
  /** By default, links will open in a new tab. Set to `false` to open links in the current tab instead. */
  openLinksInCurrentTab?: boolean
  /** Class(es) to apply to the container element. */
  className?: string
  /** Extensions to apply to the markdown **/
  extensions?: readonly CopilotMarkdownExtension[]
  /** Show the streaming cursor and buffer incoming content. */
  isStreaming?: boolean
  /** Chat mode, for telemetry. */
  chatMode?: 'assistive' | 'immersive'
  /** Optional invisible header value for screen readers */
  accessibleHeader?: string
  /** Annotations to show in the code blocks. */
  copilotAnnotations?: CopilotAnnotations
  /** Whether or not code lines should be wrapped in CodeBlocks */
  wrapCodeLines?: boolean
  /** Callback when the wrapCodeLines setting is changed */
  onWrapCodeLinesChange?: (wrap: boolean) => void
  ref?: React.Ref<HTMLDivElement>
}

const emptyArray = [] as const

export const STREAMING_FADE_DURATION_MS = 750

export function MarkdownRenderer({
  ref,
  className,
  markdown,
  chatMode,
  openLinksInCurrentTab,
  onLinkClick,
  extensions: additionalExtensions = emptyArray,
  isStreaming,
  accessibleHeader,
  copilotAnnotations,
  wrapCodeLines,
  onWrapCodeLinesChange,
}: MarkdownRendererProps) {
  // Keep the fade-in animation class activated until the fade completes
  const [fadingInContent, setFadingInContent] = useState(isStreaming ?? false)
  useEffect(() => {
    if (!isStreaming) {
      const timeout = setTimeout(() => {
        setFadingInContent(false)
      }, STREAMING_FADE_DURATION_MS)
      return () => clearTimeout(timeout)
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFadingInContent(true)
    }
  }, [isStreaming])

  const extensions = useMemo(() => {
    return [
      // More specific extensions must go last! So math (which uses `math` code blocks) and files (which use
      // `lang name=filename` code blocks) must follow the codeBlocksExtension
      codeBlocksExtension(),
      mathExtension(),
      linksExtension({openLinksInCurrentTab}),
      streamingExtension({isStreaming: fadingInContent}),
      imagesExtension(),
      ...additionalExtensions,
    ]
  }, [additionalExtensions, openLinksInCurrentTab, fadingInContent])

  const remarkPlugins: PluggableList = [
    remarkGfm,
    remarkCjkFriendly,
    remarkCjkFriendlyGfmStrikethrough,
    ...extensions.map(e => (e.transformMarkdown ? () => e.transformMarkdown : undefined)).filter(e => !!e),
  ]

  const {isBrowser} = useRuntimeEnvironment()
  const highlightPlugin = useRehypeHighlight()
  const rehypePlugins: PluggableList = useMemo(() => {
    if (!isBrowser) return []
    const plugins: PluggableList = [
      ...extensions.map(e => (e.transformHtml ? () => e.transformHtml : undefined)).filter(e => !!e),
    ]
    if (highlightPlugin) plugins.unshift(highlightPlugin)
    return plugins
  }, [extensions, isBrowser, highlightPlugin])

  const components = useCombinedComponents(extensions)

  const preprocessedMarkdown = extensions.reduce((md, ext) => ext.preprocessMarkdown?.(md) ?? md, markdown)

  const containerRef = useRef<HTMLDivElement>(null)

  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
    },
    [ref],
  )

  useEffect(() => {
    if (!onLinkClick) return
    const container = containerRef.current
    if (!container) return

    const handleClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest('a')
      if (target && container.contains(target)) {
        onLinkClick(event)
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [onLinkClick])

  return (
    <ExtensionContext
      value={useMemo(
        () => ({isStreaming, chatMode, copilotAnnotations, wrapCodeLines, onWrapCodeLinesChange}),
        [isStreaming, chatMode, copilotAnnotations, wrapCodeLines, onWrapCodeLinesChange],
      )}
    >
      {accessibleHeader && <h3 className="sr-only">{accessibleHeader}</h3>}
      <div
        ref={mergedRef}
        className={clsx('markdown-body', styles.container, className, fadingInContent && styles.fadeInContent)}
        style={{
          '--MarkdownRenderer_streaming-fade-duration': `${STREAMING_FADE_DURATION_MS}ms`,
        }}
        data-copilot-markdown
      >
        <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
          {preprocessedMarkdown}
        </ReactMarkdown>
      </div>
    </ExtensionContext>
  )
}
